/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Button, Grid } from '@mui/material';
import { CloseIcon } from 'src/icons';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import * as yup from 'yup';
import { PayType } from 'src/pages/CandidateInfo/types';
import { Option } from 'src/components/elements/Select';
import { Option as CurrencyOption, options as currencyOptions } from 'src/components/elements/Input';
import { scrollToError } from 'src/utils/FormUtils';
import RHFEditor from '../elements/Editor/RHFEditor';
import RHFEmailsChips from '../elements/EmailsChips/RHFEmailsChips';
import RHFInput from '../elements/Input/RHFInput';
import RHFSelect from '../elements/Select/RHFSelect';
import Loader from '../Loader';

export interface emailValues {
  to: Array<string>;
  cc?: Array<string> | null;
  subject: string;
  content: string;
}

export interface AuthorizationAndRTRFormValues extends emailValues {
  payRate: string;
  visaStatus: string;
  payType: string;
  currency: string;
}

const validationSchema = yup.object().shape({
  to: yup.array(yup.string().email()).required('Must have at least 1 email'),
  cc: yup.array(yup.string().email()).optional(),
  subject: yup.string().required('subject required').min(1, 'Subject required'),
  content: yup.string().required('email content required'),
  payRate: yup.string().required('Pay Rate is required'),
  payType: yup.string().required('Pay Type is required'),
  visaStatus: yup.string().required('Work Authorizarization is required'),
});

interface AuthorizationAndRTRProps {
  payTypes: PayType[];
  workAuthorizations: string[];
  onSubmit: (data: AuthorizationAndRTRFormValues) => void;
  onCancel: () => void;
  loading: boolean;
  defaultValues?: AuthorizationAndRTRFormValues | undefined;
  title?: string;
}

const AuthorizationAndRTR = function AuthorizationAndRTR(props: AuthorizationAndRTRProps) {
  const [loading, setLoading] = useState(false);
  const {
    payTypes,
    workAuthorizations,
    onSubmit,
    defaultValues,
    onCancel,
    title,
  } = props;
  const resolver = useYupValidationResolver(validationSchema);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyOption>(
    currencyOptions.find((option) => option.currencyName === defaultValues?.currency) || { currencyName: 'USD', currencyIcon: '$' }
  );

  const methods = useForm<AuthorizationAndRTRFormValues>({
    defaultValues,
    resolver,
  });

  const {
    handleSubmit,
    getValues,
    clearErrors,
  } = methods;

  const onError = () => {
    scrollToError('authorization-form');
  };

  const payTypesArray: Option<string, string>[] = payTypes?.map((each) => ({
    label: each.name,
    value: (each.id).toString(),
  }));

  const workAuthorizationsArray: Option<string, string>[] = workAuthorizations?.map((each) => ({
    label: each,
    value: each,
  }));

  useEffect(() => {
    // set the how value in the template
    methods.setValue('to', defaultValues?.to || []);
    methods.setValue('cc', defaultValues?.cc);
    methods.setValue('subject', defaultValues?.subject || '');
    methods.setValue('payRate', defaultValues?.payRate || '');
    methods.setValue('payType', payTypesArray.find(({ label }) => defaultValues?.payType === label)?.value || '');
    methods.setValue('visaStatus', defaultValues?.visaStatus || '');
  }, [defaultValues]);

  const submitHandler: SubmitHandler<AuthorizationAndRTRFormValues> = async (data) => {
    setDisableSubmit(true);
    const request = {
      ...data,
      currency: selectedCurrency.currencyName,
      payType: data?.payType
    };
    await onSubmit(request);
    setDisableSubmit(false);
  };

  const replaceTemplateValues = (template: string, key: string, value: string) => (
    template.replaceAll(key, value)
  );

  const getValueFromFormAndUpdateTemplate = (dynamicKey: string, value: string) => {
    let modified = replaceTemplateValues(
      defaultValues?.content || '',
      dynamicKey,
      value
    );
    const {
      payRate,
      payType,
    } = getValues();

    const dynamicKeys = {
      payRate: '{{ATS_PAY_RATE}}',
      payType: '{{PAY_TYPE}}',
      currency: '{{CURRENCY}}',
    };

    const valuesObj = {
      payRate,
      payType,
      currency: selectedCurrency.currencyName,
    };

    Object.keys(dynamicKeys).map((key) => {
      const typedKey = key as keyof typeof dynamicKeys;
      if (valuesObj[typedKey]) {
        if (key === 'payType') {
          modified = replaceTemplateValues(
            modified,
            dynamicKeys[typedKey],
            payTypesArray.find(({ value }) => payType === value)?.label || '',
          );
        } else if (key === 'currency') {
          modified = replaceTemplateValues(
            modified,
            dynamicKeys[typedKey],
            selectedCurrency.currencyName,
          );
        } else {
          modified = replaceTemplateValues(
            modified,
            dynamicKeys[typedKey],
            valuesObj[typedKey],
          );
        }
      }
    });

    methods.setValue('content', modified);
  }

  return (
    <div id="authorization-form">
      <FormProvider {...methods}>
        <form className="bg-white  p-10 flex gap-8 justify-center items-center flex-col">
          {title && (
            <div className="flex justify-center items-center w-full text-[#28364C] font-medium text-base">
              {title}
            </div>
          )}

          <div className="flex w-full p-4 flex-col gap-10">
            <div className="w-full flex-shrink-0">
              <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <RHFInput
                    name="payRate"
                    iconStart={selectedCurrency?.currencyIcon || '$'}
                    control={methods.control}
                    label="Pay Rate"
                    onCurrencyChange={
                      (currency: CurrencyOption) => {
                        setSelectedCurrency(currency);
                        getValueFromFormAndUpdateTemplate(
                          '{{CURRENCY}}',
                          currency.currencyName || '',
                        );
                      }
                    }
                    onChange={(event: any) => {
                      clearErrors('payRate');
                      methods.setValue('payRate', event);
                      getValueFromFormAndUpdateTemplate(
                        '{{ATS_PAY_RATE}}',
                        event,
                      );
                    }}
                    disabled={Boolean(defaultValues?.payRate)}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <RHFSelect
                    control={methods.control}
                    name="payType"
                    label="Pay Type"
                    options={payTypesArray}
                    onSelectChange={(event: any) => {
                      getValueFromFormAndUpdateTemplate(
                        '{{PAY_TYPE}}',
                        payTypesArray.find(({ value }) => event === value)?.label || '',
                      );
                    }}
                    disabled={Boolean(defaultValues?.payType)}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <RHFSelect
                    control={methods.control}
                    name="visaStatus"
                    label="Work Authorization"
                    options={workAuthorizationsArray}
                    // onSelectChange={(event: any) => {
                    //   getValueFromFormAndUpdateTemplate(
                    //     '{{}}',
                    //     event,
                    //   );
                    // }}
                    disabled={Boolean(defaultValues?.visaStatus)}
                  />
                </Grid >
              </Grid >
            </div >

            <div className="w-full flex-shrink-0">
              <RHFEmailsChips
                control={methods.control}
                name="to"
                label="To"
                placeholder="Enter emails"
              />
            </div>
            <div className="w-full flex-shrink-0">
              <RHFEmailsChips
                control={methods.control}
                name="cc"
                label="cc"
                placeholder="Enter emails"
              />
            </div>
            <div className="w-full flex-shrink-0">
              <RHFInput
                control={methods.control}
                name="subject"
                label="Subject"
                placeholder="Subject"
              />
            </div>
            <div className="w-full min-h-[200px] flex-shrink-0">
              <RHFEditor
                control={methods.control}
                name="content"
              />
            </div>
          </div >
          <div className="w-full flex  gap-2 ">
            <Button variant="contained" type="submit" size="small" className="font-medium rounded-full" disabled={disableSubmit} onClick={handleSubmit(submitHandler, onError)}>Send</Button>
            <Button variant="outlined" size="small" className="font-medium rounded-full" onClick={onCancel} disabled={disableSubmit}>Cancel</Button>
          </div>
          <div
            className="absolute right-2 top-2"
            onClick={onCancel}
          >
            <CloseIcon className="cursor-pointer" />
          </div>
        </form >
      </FormProvider >

      {loading && (
        <div className="fixed top-[50vh] left-[50vw]">
          <Loader />
        </div>
      )}
    </div>
  );
};

AuthorizationAndRTR.defaultProps = {
  title: '',
};

AuthorizationAndRTR.propTypes = {

};

export default AuthorizationAndRTR;
